import { DatePipe } from '@angular/common';
import { Component, computed, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { XlsxService } from '@reflact/ngx-xlsx';
import {
    AVATAR_LATENCY_DETAIL_PHASES,
    AVATAR_LATENCY_MAIN_PHASES,
    AvatarLatencyDetailPhaseKey,
    AvatarLatencyInputMode,
    AvatarLatencyMainPhaseKey,
    AvatarLatencyPhaseKey,
    AvatarLatencyProvider,
    AvatarLatencySummary,
    AvatarLatencyTurnSummary,
    avatarLatencyMainPhase,
    percentile
} from '@shared/index';
import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexPlotOptions, ApexTooltip, ApexXAxis, ApexYAxis, NgApexchartsModule } from 'ng-apexcharts';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ManualLinkComponent } from 'src/app/shared/components/rag-admin/manual-link/manual-link.component';
import { StatisticService } from '../statistics-tab/statistic.service';

type Option<T> = { label: string; value: T };

const PHASE_LABELS: Record<AvatarLatencyPhaseKey, string> = {
    input: $localize`:@@latency-phase-input:Spracheingabe`,
    ai: $localize`:@@latency-phase-ai:KI-Antwort`,
    speech: $localize`:@@latency-phase-speech:Sprachausgabe`,
    avatar: $localize`:@@latency-phase-avatar:Avatar-Start`,
    silenceWait: $localize`:@@latency-phase-silence-wait:Warten auf Stille`,
    stt: $localize`:@@latency-phase-stt:Spracherkennung`,
    prep: $localize`:@@latency-phase-prep:Vorbereitung (Thread, Tools, Dokumentsuche)`,
    llmFirstToken: $localize`:@@latency-phase-llm-first-token:KI bis zum ersten Wort`,
    llmRest: $localize`:@@latency-phase-llm-rest:KI bis fertig`,
    speechGen: $localize`:@@latency-phase-speech-gen:Sprachausgabe erzeugen`,
    avatarStart: $localize`:@@latency-phase-avatar-start:Avatar-Start`,
    rest: $localize`:@@latency-phase-rest:Netzwerk/Rest`
};

const PHASE_COLORS: Record<AvatarLatencyPhaseKey, string> = {
    input: '#6c9bd2',
    ai: '#8e7cc3',
    speech: '#f6b26b',
    avatar: '#93c47d',
    silenceWait: '#a4c2f4',
    stt: '#6c9bd2',
    prep: '#b4a7d6',
    llmFirstToken: '#8e7cc3',
    llmRest: '#674ea7',
    speechGen: '#f6b26b',
    avatarStart: '#93c47d',
    rest: '#cccccc'
};

@Component({
    imports: [FormsModule, DatePipe, NgApexchartsModule, ButtonModule, CardModule, ManualLinkComponent, MessageModule, SelectModule, TableModule, ToggleSwitchModule, TooltipModule],
    templateUrl: './latency-tab.component.html'
})
export class LatencyTabComponent implements OnInit {
    private statService = inject(StatisticService);
    private rss = inject(RouteShareService);
    private toastr = inject(ToastrService);
    private locale = inject(LOCALE_ID);
    private xlsxService = inject(XlsxService);

    protected days = 30;
    protected provider: AvatarLatencyProvider | null = null;
    protected inputMode: AvatarLatencyInputMode | null = null;

    protected readonly dayOptions: Option<number>[] = [
        { label: $localize`:@@latency-period-7:Letzte 7 Tage`, value: 7 },
        { label: $localize`:@@latency-period-30:Letzte 30 Tage`, value: 30 },
        { label: $localize`:@@latency-period-90:Letzte 90 Tage`, value: 90 }
    ];
    protected readonly providerOptions: Option<AvatarLatencyProvider | null>[] = [
        { label: $localize`:@@latency-provider-all:Alle Avatare`, value: null },
        { label: 'LiveAvatar', value: 'liveavatar' },
        { label: 'D-ID', value: 'did' }
    ];
    protected readonly inputModeOptions: Option<AvatarLatencyInputMode | null>[] = [
        { label: $localize`:@@latency-input-all:Alle Eingaben`, value: null },
        { label: $localize`:@@latency-input-voice:Spracheingabe`, value: 'voice' },
        { label: $localize`:@@latency-input-text:Texteingabe`, value: 'text' }
    ];

    protected readonly summary = signal<AvatarLatencySummary | null>(null);
    protected readonly showDetails = signal(false);
    protected readonly detailPhases = AVATAR_LATENCY_DETAIL_PHASES;
    protected readonly phaseLabels = PHASE_LABELS;
    protected readonly phaseColors = PHASE_COLORS;

    private readonly numberFormat = new Intl.NumberFormat(this.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

    protected readonly phaseRows = computed<{ key: AvatarLatencyPhaseKey; p50: number; p90: number }[]>(() => {
        const summary = this.summary();
        if (!summary) return [];
        if (this.showDetails()) return summary.phases;
        return AVATAR_LATENCY_MAIN_PHASES.map((key: AvatarLatencyMainPhaseKey) => {
            const values = summary.turns.map(t => avatarLatencyMainPhase(t.detail, key));
            return { key, p50: percentile(values, 50), p90: percentile(values, 90) };
        });
    });

    protected readonly phaseSeries = computed<ApexAxisChartSeries>(() =>
        this.phaseRows().map(row => ({ name: PHASE_LABELS[row.key], data: [row.p50 / 1000, row.p90 / 1000], color: PHASE_COLORS[row.key] }))
    );

    protected readonly scatterSeries = computed<ApexAxisChartSeries>(() => {
        const turns = this.summary()?.turns ?? [];
        return [{
            name: $localize`:@@latency-scatter-series:Gefühlte Wartezeit`,
            data: turns.map(t => [t.answerChars ?? 0, Math.round(t.perceivedMs / 100) / 10])
        }];
    });

    protected readonly recentTurns = computed(() => (this.summary()?.turns ?? []).slice(0, 50));

    protected readonly barChart: ApexChart = { type: 'bar', stacked: true, height: 240, toolbar: { show: false }, animations: { enabled: false } };
    protected readonly barPlotOptions: ApexPlotOptions = { bar: { horizontal: true, barHeight: '60%' } };
    protected readonly barXAxis: ApexXAxis = {
        categories: [$localize`:@@latency-median:Median`, $localize`:@@latency-p90:90 % der Runden`],
        labels: { formatter: (val: string) => `${val} s` }
    };
    protected readonly barDataLabels: ApexDataLabels = {
        enabled: true,
        formatter: (val: number) => (val >= 0.3 ? this.numberFormat.format(val) : '')
    };
    protected readonly secondsTooltip: ApexTooltip = { y: { formatter: (val: number) => `${this.numberFormat.format(val)} s` } };

    protected readonly scatterChart: ApexChart = { type: 'scatter', height: 300, toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } };
    protected readonly scatterXAxis: ApexXAxis = {
        type: 'numeric',
        title: { text: $localize`:@@latency-scatter-x:Antwortlänge (Zeichen)` },
        tickAmount: 6
    };
    protected readonly scatterYAxis: ApexYAxis = {
        title: { text: $localize`:@@latency-scatter-y:Gefühlte Wartezeit (s)` },
        min: 0
    };

    public async ngOnInit(): Promise<void> {
        await this.load();
    }

    protected async load(): Promise<void> {
        try {
            this.summary.set(await this.statService.getAvatarLatency(this.rss.botConfig._id, { days: this.days, provider: this.provider, inputMode: this.inputMode }));
        } catch {
            this.toastr.error('GENERAL', $localize`:@@latency-load-error:Die Antwortzeiten konnten nicht geladen werden. Versuche es erneut.`);
        }
    }

    protected seconds(ms: number | undefined): string {
        return ms === undefined ? '–' : `${this.numberFormat.format(ms / 1000)} s`;
    }

    protected percent(share: number | null): string {
        return share === null ? '–' : `${String(Math.round(share * 100))} %`;
    }

    protected segmentWidth(turn: AvatarLatencyTurnSummary, key: AvatarLatencyDetailPhaseKey): number {
        // Summe statt perceivedMs: bei Messungenauigkeiten (Rest auf 0 gekappt) bleibt der Balken bei 100 %
        const total = AVATAR_LATENCY_DETAIL_PHASES.reduce((sum, k) => sum + turn.detail[k], 0);
        return total > 0 ? (turn.detail[key] / total) * 100 : 0;
    }

    protected phaseTooltip(turn: AvatarLatencyTurnSummary, key: AvatarLatencyDetailPhaseKey): string {
        return `${PHASE_LABELS[key]}: ${this.seconds(turn.detail[key])}`;
    }

    protected providerLabel(provider: AvatarLatencyProvider | undefined): string {
        return provider === 'did' ? 'D-ID' : 'LiveAvatar';
    }

    protected inputModeLabel(mode: AvatarLatencyInputMode | undefined): string {
        return mode === 'voice' ? $localize`:@@latency-input-voice:Spracheingabe` : $localize`:@@latency-input-text:Texteingabe`;
    }

    protected exportTurns(): void {
        const turns = this.summary()?.turns ?? [];
        const toSeconds = (ms: number) => Math.round(ms) / 1000;
        const config = [
            { fieldName: 'created', label: $localize`:@@latency-col-time:Zeitpunkt` },
            { fieldName: 'provider', label: $localize`:@@latency-col-provider:Avatar` },
            { fieldName: 'inputMode', label: $localize`:@@latency-col-input:Eingabe` },
            { fieldName: 'perceived', label: $localize`:@@latency-export-perceived:Gefühlte Wartezeit (s)` },
            { fieldName: 'system', label: $localize`:@@latency-export-system:Systemlatenz (s)` },
            ...AVATAR_LATENCY_DETAIL_PHASES.map(key => ({ fieldName: key, label: `${PHASE_LABELS[key]} (s)` })),
            { fieldName: 'answerChars', label: $localize`:@@latency-col-chars:Zeichen` },
            { fieldName: 'toolRounds', label: $localize`:@@latency-col-tools:Tool-Runden` },
            { fieldName: 'docSearchUsed', label: $localize`:@@latency-col-docsearch:Dokumentsuche` },
            { fieldName: 'llmModel', label: $localize`:@@latency-col-model:Modell` },
            { fieldName: 'ttsModel', label: $localize`:@@latency-col-tts:TTS-Modell` }
        ];
        const data = turns.map(turn => ({
            created: new Date(turn.created).toLocaleString(this.locale),
            provider: this.providerLabel(turn.provider),
            inputMode: this.inputModeLabel(turn.inputMode),
            perceived: toSeconds(turn.perceivedMs),
            system: toSeconds(turn.systemMs),
            ...Object.fromEntries(AVATAR_LATENCY_DETAIL_PHASES.map(key => [key, toSeconds(turn.detail[key])])),
            answerChars: turn.answerChars ?? '',
            toolRounds: turn.toolRounds ?? 0,
            docSearchUsed: turn.docSearchUsed ? $localize`:@@latency-export-yes:Ja` : $localize`:@@latency-export-no:Nein`,
            llmModel: turn.llmModel ?? '',
            ttsModel: turn.ttsModel ?? ''
        }));
        const botName = this.rss.botConfig.name.replace(/[^\p{L}\p{N}_-]+/gu, '-');
        try {
            this.xlsxService.exportXlsx(data, config, `antwortzeiten-${botName}-${new Date().toISOString().slice(0, 10)}`);
        } catch {
            this.toastr.error('GENERAL', $localize`:@@latency-export-error:Der Export ist fehlgeschlagen. Versuche es erneut.`);
        }
    }
}
